/**
 * engine.api.ts — Unified Workflow Engine client (V2 architecture, Stage 5).
 *
 * ONE typed client for EVERY workflow — confirmation, shipping_confirmation,
 * sales, customer_service, delivery_tracking, and any merchant-defined
 * workflow. The workflowId is a parameter, never a special case.
 *
 * Identity: the DT OS Bearer token (sent automatically by client.ts) identifies
 * the caller on every request. No agentId header or query param needed.
 */

import { get, post, patch, apiRequest } from './client'

// ── Core types (mirror engine/types.ts) ───────────────────────────────────────

export type WorkflowId =
  | 'confirmation' | 'shipping_confirmation' | 'sales'
  | 'customer_service' | 'delivery_tracking'
  | (string & {})

export type EnginePhase =
  | 'created' | 'queued' | 'assigned' | 'in_session'
  | 'escalated' | 'resolved' | 'archived'

export type OrderRef = {
  shopifyOrderId: string
  orderName:      string
  rowIndex:       number
}

export type AttemptState = {
  type?: 'no_signal' | 'no_answer' | 'busy'
  count: number; day: number; today: number; dailyQuota: number
  lastAt?: string; nextRetryAt?: string; exhausted?: boolean
}

export type QueueEntry = {
  id:         string
  workflowId: WorkflowId
  orderRef:   OrderRef
  phase:      EnginePhase
  status:     string
  addedAt:    string
  updatedAt:  string
  segmentId?:  string
  assignedTo?: string
  priority?:   'normal' | 'high' | 'critical'
  postponedUntil?:    string
  postponementCount?: number
  attempts?:          AttemptState
  customStatusName?:  string
  lastNote?:          string
  meta: Record<string, unknown>
}

export type WorkflowSession = {
  id:           string
  workflowId:   WorkflowId
  queueEntryId: string
  agentId:      string
  openedAt:     string
  closedAt?:    string
  outcomeId?:   string
  customStatusName?: string
  note?:        string
  postponedUntil?: string
  escalationId?: string
  meta: Record<string, unknown>
}

export type StatusDefinition = {
  id: string; name: string; color: string; icon?: string; order: number
  phase: EnginePhase
  isTerminal: boolean; isSuccess: boolean; isFailure: boolean
  isPaused: boolean; isWaiting: boolean; isEscalated: boolean; retryable: boolean
  allowedTransitions: string[]
  visible: boolean
}

export type StatusSet = { id: string; name: string; initialStatusId: string; statuses: StatusDefinition[] }

export type OutcomeDefinition = {
  id: string; name: string
  actions: { type: string; [k: string]: unknown }[]
}

export type WorkflowCapabilities = {
  queue: boolean; assignment: boolean; sessions: boolean; presence: boolean
  distribution: boolean; escalationOrigin: boolean; escalationDestination: boolean
  unreachableTracking: boolean
}

export type WorkflowDefinition = {
  id: WorkflowId; name: string; enabled: boolean
  capabilities: WorkflowCapabilities
  sheet: { statusField: string; activitySheet: string }
  statusSetId: string
  outcomeIds: string[]
  extensionSchema: { key: string; type: string; required?: boolean; label?: string }[]
}

export type WorkflowRuntime = {
  paused: boolean; pausedBy?: string; pausedAt?: string
  lastCronAt?: string
  lastCronResult?: { added: number; errors: number }
  lastError?: { at: string; message: string; component: string }
  health: 'ok' | 'degraded' | 'failing'
}

// Schema-driven UI: the generic workflow page renders columns/filters from
// here. A new workflow appears with ZERO frontend changes; `if (workflow===..)`
// in the UI is a design failure by definition (owner requirement #1).
export type UiColumnSpec = {
  key:    string
  source: 'entry' | 'orderRef' | 'meta'
  label:  string
  type:   'text' | 'order_link' | 'status_badge' | 'phase_badge'
        | 'agent' | 'datetime' | 'relative_time' | 'attempts' | 'note' | 'priority'
  width?: 'xs' | 'sm' | 'md' | 'lg'
}

export type UiFilterSpec = { key: string; label: string }

export type UiTabSpec = {
  id:     string
  label:  string
  phase?:  string[]
  status?: string
  meta?:   Record<string, string>
}

export type UiBulkActionSpec = {
  id:         string
  label:      string
  command:    string
  permission: Permission
}

export type UiWidgetSpec = {
  id:    string
  label: string
  type:  'metric'
  metric: {
    subject: 'entries' | 'sessions'
    groupBy: string
    todayOnly?: boolean
  }
}

export type UiSortField = 'addedAt' | 'priority' | 'updatedAt' | 'attempts' | 'postponedUntil'

export type UiSortSpec = {
  field:     UiSortField
  direction: 'asc' | 'desc'
}

export type WorkflowUiSpec = {
  queueColumns:  UiColumnSpec[]
  filters:       UiFilterSpec[]
  tabs?:         UiTabSpec[]
  bulkActions?:  UiBulkActionSpec[]
  widgets?:      UiWidgetSpec[]
  defaultSort?:  UiSortSpec[]
}

export type EscalationReason = {
  id:                  string
  name:                string
  description?:        string
  priority:            'normal' | 'high' | 'critical'
  destinationWorkflow: WorkflowId
  enabled:             boolean
  order:               number
  fields:              { id: string; label: string; type: string; required: boolean; order: number }[]
}

export type WorkflowSurface = {
  definition:        WorkflowDefinition
  ui:                WorkflowUiSpec
  statusSet:         StatusSet
  outcomes:          OutcomeDefinition[]
  runtime:           WorkflowRuntime
  escalationReasons: EscalationReason[]
}

// Permission list for the current employee — the page shows/hides controls
// from this array, never from role names or workflow ids.
export type Permission =
  | 'queue.view' | 'queue.add' | 'queue.assign' | 'queue.distribute'
  | 'session.work' | 'escalation.create' | 'escalation.manage'
  | 'presence.self' | 'presence.manage'
  | 'settings.view' | 'settings.edit'
  | 'workflow.pause' | 'analytics.view' | 'admin.migrate'
  | 'order.create' | 'order.exchange' | 'order.return' | 'order.edit'

export type WorkflowPermissions = {
  bootstrap:  boolean
  employeeId: string
  granted:    Permission[]
}

export type WorkflowDashboard = {
  workflowId: WorkflowId
  runtime:    WorkflowRuntime
  byPhase:    Record<string, number>
  byStatus:   Record<string, number>
  today:      { sessionsClosed: number; byOutcome: Record<string, number> }
  agentsOnline: number
}

export type AgentPresence = {
  agentId: string; workflowId: WorkflowId
  state: 'idle' | 'working' | 'on_break'
  startedAt: string | null; breakStartedAt: string | null
  lastAllocationDate: string | null; dailyOrderCount: number; checkedInAt: string | null
}

export type Agent = {
  id: string; displayName: string; roleId: string
  workflows: WorkflowId[]; active: boolean
  identity: { provider: string; externalId?: string }
  createdAt: string; updatedAt: string
}

export type TimelineEvent = {
  at: string
  kind: 'entry' | 'session_open' | 'session_close' | 'escalation'
  workflowId: WorkflowId
  detail: Record<string, unknown>
}

export type EscalationEntry = {
  id: string
  originWorkflow: WorkflowId; originQueueEntryId: string
  originOrderId: string; originOrderName: string; originAgentId: string
  destinationWorkflow: WorkflowId
  reasonId: string; reasonName: string
  formData: { fieldId: string; label: string; value: string | number | boolean | null }[]
  priority: 'normal' | 'high' | 'critical'
  assignedAgentId?: string
  status: 'pending' | 'assigned' | 'in_progress' | 'completed' | 'returned' | 'cancelled'
  createdAt: string; updatedAt: string
  acceptedAt?: string; completedAt?: string; returnedAt?: string
  lastNote?: string
}

export type CloseSessionBody = {
  outcomeId:         string
  postponedUntil?:   string
  note?:             string
  customStatusName?: string
  unreachableType?:  'no_signal' | 'no_answer' | 'busy'
  escalation?: {
    reasonId:  string
    formData?: EscalationEntry['formData']
    priority?: EscalationEntry['priority']
  }
  meta?: Record<string, unknown>
}

export type WorkflowMetrics = {
  workflowId:   string
  shop:         string
  queued:       number
  active:       number
  postponed:    number
  closedToday: {
    total:     number
    byOutcome: Record<string, number>
  }
}

export type DeliveryHistoryItem = {
  orderName:        string
  orderId:          string
  trackingNumber:   string
  carrierName:      string
  integrationType:  string
  deliveryStatus:   string
  customerName:     string
  city:             string
  scanPickupAt:     string
  pickedUpAt:       string
  inTransitAt:      string
  outForDeliveryAt: string
  deliveredAt:      string
  failedAt:         string
  returnedAt:       string
  scanReturnAt:     string
  failedReason:     string
  failedOutcome:    string
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function qs(shop: string, params: Record<string, string | undefined> = {}): string {
  const p = new URLSearchParams({ shop })
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') p.set(k, v)
  return `?${p.toString()}`
}

// ── The unified API ───────────────────────────────────────────────────────────

export const engineApi = {
  // Workflow shell
  workflows: (shop: string): Promise<{ items: WorkflowDefinition[]; total: number }> =>
    get(`/workflows${qs(shop)}`),

  surface: (shop: string, wf: WorkflowId): Promise<WorkflowSurface> =>
    get(`/workflows/${wf}/surface${qs(shop)}`),

  dashboard: (shop: string, wf: WorkflowId): Promise<WorkflowDashboard> =>
    get(`/workflows/${wf}/dashboard${qs(shop)}`),

  metrics: (shop: string, wf: WorkflowId): Promise<WorkflowMetrics> =>
    get(`/workflows/${wf}/metrics${qs(shop)}`),

  deliveryHistory: (shop: string): Promise<{ items: DeliveryHistoryItem[]; total: number }> =>
    get(`/workflows/delivery_tracking/history${qs(shop)}`),

  runtime: (shop: string, wf: WorkflowId): Promise<WorkflowRuntime> =>
    get(`/workflows/${wf}/runtime${qs(shop)}`),

  pause: (shop: string, wf: WorkflowId): Promise<WorkflowRuntime> =>
    post(`/workflows/${wf}/runtime/pause${qs(shop)}`, {}),

  resume: (shop: string, wf: WorkflowId): Promise<WorkflowRuntime> =>
    post(`/workflows/${wf}/runtime/resume${qs(shop)}`, {}),

  // Queue — paginated always (limit ≤ 200, default 50); meta filters use
  // declared extensionSchema keys ('meta:<key>').
  queue: (
    shop: string, wf: WorkflowId,
    filters?: {
      phase?: EnginePhase[]; status?: string; agent?: string; segmentId?: string
      meta?: Record<string, string>
      limit?: number; offset?: number
      sort?: UiSortSpec[]
    },
  ): Promise<{ items: QueueEntry[]; total: number; limit: number; offset: number }> =>
    get(`/workflows/${wf}/queue${qs(shop, {
      phase:     filters?.phase?.join(','),
      status:    filters?.status,
      agent:     filters?.agent,
      segmentId: filters?.segmentId,
      limit:     filters?.limit?.toString(),
      offset:    filters?.offset?.toString(),
      sort:      filters?.sort?.map(s => `${s.field}:${s.direction}`).join(','),
      ...Object.fromEntries(Object.entries(filters?.meta ?? {}).map(([k, v]) => [`meta:${k}`, v])),
    })}`),

  addToQueue: (
    shop: string, wf: WorkflowId,
    body: { shopifyOrderId: string; orderName: string; priority?: 'normal' | 'high' | 'critical'; meta?: Record<string, unknown> },
  ): Promise<{ entry: QueueEntry; created: boolean }> =>
    post(`/workflows/${wf}/queue${qs(shop)}`, body),

  assign: (shop: string, wf: WorkflowId, entryId: string, targetEmployeeId: string): Promise<QueueEntry> =>
    post(`/workflows/${wf}/queue/${entryId}/assign${qs(shop)}`, { agentId: targetEmployeeId }),

  unassign: (shop: string, wf: WorkflowId, entryId: string): Promise<QueueEntry> =>
    post(`/workflows/${wf}/queue/${entryId}/unassign${qs(shop)}`, {}),

  reopen: (shop: string, wf: WorkflowId, entryId: string): Promise<QueueEntry> =>
    post(`/workflows/${wf}/queue/${entryId}/reopen${qs(shop)}`, {}),

  // Sessions
  sessions: (
    shop: string, wf: WorkflowId,
    filters?: { agent?: string; entryId?: string; open?: boolean; limit?: number; offset?: number },
  ): Promise<{ items: WorkflowSession[]; total: number; limit: number; offset: number }> =>
    get(`/workflows/${wf}/sessions${qs(shop, {
      agent: filters?.agent, entryId: filters?.entryId,
      open: filters?.open ? 'true' : undefined,
      limit: filters?.limit?.toString(),
      offset: filters?.offset?.toString(),
    })}`),

  openSession: (shop: string, wf: WorkflowId, entryId: string):
    Promise<{ session: WorkflowSession; created: boolean }> =>
    post(`/workflows/${wf}/queue/${entryId}/session/open${qs(shop)}`, {}),

  annotateSession: (shop: string, wf: WorkflowId, sessionId: string, customStatusName: string):
    Promise<{ ok: true }> =>
    post(`/workflows/${wf}/sessions/${sessionId}/annotate${qs(shop)}`, { customStatusName }),

  closeSession: (shop: string, wf: WorkflowId, sessionId: string, body: CloseSessionBody):
    Promise<{ session: WorkflowSession }> =>
    post(`/workflows/${wf}/sessions/${sessionId}/close${qs(shop)}`, body),

  // Presence + distribution
  presenceBoard: (shop: string, wf: WorkflowId):
    Promise<{ board: { agentId: string; displayName: string; presence: AgentPresence; activeOrders: number }[] }> =>
    get(`/workflows/${wf}/presence${qs(shop)}`),

  checkIn: (shop: string, wf: WorkflowId): Promise<AgentPresence> =>
    post(`/workflows/${wf}/presence/check-in${qs(shop)}`, {}),

  startWork: (shop: string, wf: WorkflowId):
    Promise<{ presence: AgentPresence; pulled: number; activeOrders: number }> =>
    post(`/workflows/${wf}/presence/start-work${qs(shop)}`, {}),

  checkOut: (shop: string, wf: WorkflowId):
    Promise<{ presence: AgentPresence; releasedOrders: number }> =>
    post(`/workflows/${wf}/presence/check-out${qs(shop)}`, {}),

  startBreak: (shop: string, wf: WorkflowId): Promise<AgentPresence> =>
    post(`/workflows/${wf}/presence/break/start${qs(shop)}`, {}),

  endBreak: (shop: string, wf: WorkflowId): Promise<AgentPresence> =>
    post(`/workflows/${wf}/presence/break/end${qs(shop)}`, {}),

  pull: (shop: string, wf: WorkflowId): Promise<{ allowance: number; pulled: number }> =>
    post(`/workflows/${wf}/distribute/pull${qs(shop)}`, {}),

  releaseAgent: (shop: string, wf: WorkflowId, targetEmployeeId: string): Promise<{ released: number }> =>
    post(`/workflows/${wf}/distribute/release${qs(shop)}`, { agentId: targetEmployeeId }),

  // Timeline
  timeline: (shop: string, shopifyOrderId: string): Promise<{ events: TimelineEvent[]; total: number }> =>
    get(`/workflows/orders/${encodeURIComponent(shopifyOrderId)}/timeline${qs(shop)}`),

  // Escalations (any workflow ⇄ any workflow)
  escalations: (
    shop: string,
    filters?: { status?: EscalationEntry['status']; origin?: WorkflowId; destination?: WorkflowId },
  ): Promise<{ items: EscalationEntry[]; total: number }> =>
    get(`/workflows/escalations${qs(shop, {
      status: filters?.status, origin: filters?.origin, destination: filters?.destination,
    })}`),

  createEscalation: (
    shop: string,
    body: { originWorkflow: WorkflowId; originQueueEntryId: string; reasonId: string; formData?: EscalationEntry['formData']; note?: string },
  ): Promise<EscalationEntry> =>
    post(`/workflows/escalations${qs(shop)}`, body),

  escalationOp: (
    shop: string, id: string,
    op: 'assign' | 'accept' | 'complete' | 'return' | 'cancel',
    body?: { agentId?: string; note?: string },
  ): Promise<EscalationEntry> =>
    post(`/workflows/escalations/${id}/${op}${qs(shop)}`, body ?? {}),

  // Permissions — effective grants for the authenticated employee in workflowId.
  // The UI renders from this list; if not granted → control hidden.
  permissions: (shop: string, wf: WorkflowId): Promise<WorkflowPermissions> =>
    get(`/workflows/${wf}/permissions${qs(shop)}`),

  // Order record — on-demand only (expand), never prefetched for table rows.
  // Queue holds identity (orderRef) not order data (owner requirement #2).
  orderRecord: (shop: string, shopifyOrderId: string): Promise<{
    fields:    Record<string, string>
    customer:  {
      name: string; phone: string; city: string; address: string
      addressFields: { firstName: string; lastName: string; phone: string; province: string; city: string; address1: string; address2: string }
    } | null
    lineItems: { id: string; sku: string; title: string; quantity: number; price: string; fulfillmentLineItemId: string | null }[]
    summary:   { subtotal: string; shipping: string; discount: string; discountCodes: string[]; total: string; paid: string; balance: string } | null
  }> =>
    get(`/workflows/orders/${encodeURIComponent(shopifyOrderId)}/record${qs(shop)}`),

  updateOrderAddress: (shop: string, orderId: string, addr: {
    firstName: string; lastName: string; phone: string
    province: string; city: string; address1: string; address2: string
  }): Promise<{ ok: boolean }> =>
    post(`/workflows/orders/${encodeURIComponent(orderId)}/address${qs(shop)}`, addr),

  editOrderItems: (shop: string, orderId: string, changes: { lineItemId: string; quantity: number }[]): Promise<{ ok: boolean }> =>
    post(`/workflows/orders/${encodeURIComponent(orderId)}/items/edit${qs(shop)}`, { changes }),

  customerHealth: (shop: string, phone: string): Promise<{
    orderCount:       number
    cancellationRate: number
    codRisk:          number
    recentOrders:     { name: string; date: string; financialStatus: string; total: string }[]
  }> =>
    get(`/workflows/orders/customer-health${qs(shop)}&phone=${encodeURIComponent(phone)}`),

  deliveryTimeline: (shop: string, trackingNumber: string): Promise<{
    trackingNumber:         string
    currentStatus:          string
    timeline:               { value: string; code: number; done: boolean; date: string; desc?: string }[]
    attempts:               { type: string; attemptDate: string; succeededAt: string | null; courier: string | null }[]
    deliveryAttemptsLength: number
    returnAttemptsLength:   number
  }> =>
    get(`/shipping/provider/delivery/${encodeURIComponent(trackingNumber)}${qs(shop)}`),

  createOrder: (
    shop:    string,
    wf:      WorkflowId,
    entryId: string,
    body:
      | {
          type:       'new'
          customer:   { name: string; phone: string; city: string; address: string }
          newItems:   { sku: string; quantity: number }[]
          priority?:  boolean
          note?:      string
        }
      | {
          type:           'exchange'
          shopifyOrderId: string
          customer:       { name: string; phone: string; city: string; address: string }
          returnItems:    { lineItemId: string; fulfillmentLineItemId: string; quantity: number; unitPrice: number; sku?: string }[]
          exchangeItems:  { sku: string; quantity: number; unitPrice: number }[]
          note?:          string
        }
      | {
          type:              'return'
          shopifyOrderId:    string
          customer:          { name: string; phone: string; city: string; address: string }
          returnLineItems:   { lineItemId: string; fulfillmentLineItemId: string; quantity: number; unitPrice: number; sku?: string }[]
          note?:             string
        },
  ): Promise<
    | { ok: true; type: 'new';      orderId: string; orderName: string }
    | { ok: true; type: 'exchange'; returnName: string; returnId: string }
    | { ok: true; type: 'return';   trackingNumber: string; providerId: string; returnId: string }
  > =>
    post(`/workflows/${wf}/queue/${entryId}/create-order${qs(shop)}`, body),

  editOrder: (
    shop:    string,
    wf:      WorkflowId,
    entryId: string,
    body: { shopifyOrderId: string; changes: { lineItemId: string; quantity: number }[] },
  ): Promise<{ ok: boolean }> =>
    patch(`/workflows/${wf}/queue/${entryId}/edit-order${qs(shop)}`, body),
}

// ── Engine Admin API — /engine-admin/* ────────────────────────────────────────
// Protected by ENGINE_ADMIN_KEY (x-engine-admin-key header).
// Key is passed explicitly per-call; never stored in module scope.

export type RegistryName =
  | 'workflows' | 'workflow_ui' | 'statuses' | 'outcomes' | 'roles'
  | 'escalation_reasons' | 'rules'

export type RegistryEnvelope<T = unknown> = {
  name:      RegistryName
  shop:      string
  version:   number
  updatedAt: string
  items:     T[]
}

export type BootstrapStatus = {
  bootstrapped: boolean
  shop:         string
  workflowCount: number
  statusSetCount: number
}

function adminRequest<T>(path: string, adminKey: string, init: RequestInit = {}): Promise<T> {
  return apiRequest<T>(path, {
    ...init,
    headers: { 'x-engine-admin-key': adminKey, 'Content-Type': 'application/json', ...init.headers },
  })
}

export const engineAdminApi = {
  status: (shop: string, adminKey: string): Promise<BootstrapStatus> =>
    adminRequest(`/engine-admin/status?shop=${encodeURIComponent(shop)}`, adminKey),

  bootstrap: (shop: string, adminKey: string): Promise<{ ok: true; message: string }> =>
    adminRequest(`/engine-admin/bootstrap?shop=${encodeURIComponent(shop)}`, adminKey, { method: 'POST', body: '{}' }),

  getRegistry: <T = unknown>(shop: string, name: RegistryName, adminKey: string): Promise<RegistryEnvelope<T>> =>
    adminRequest(`/engine-admin/registry/${name}?shop=${encodeURIComponent(shop)}`, adminKey),

  saveRegistry: <T = unknown>(
    shop: string, name: RegistryName, items: T[], adminKey: string,
  ): Promise<RegistryEnvelope<T>> =>
    adminRequest(`/engine-admin/registry/${name}?shop=${encodeURIComponent(shop)}`, adminKey, {
      method: 'POST',
      body:   JSON.stringify({ items }),
    }),

  resetBootstrap: (shop: string, adminKey: string): Promise<{ ok: true }> =>
    adminRequest(`/engine-admin/reset?shop=${encodeURIComponent(shop)}&confirm=WIPE`, adminKey, { method: 'POST', body: '{}' }),

  backfillMeta: (shop: string, adminKey: string): Promise<{ ok: true; updated: number; errors: string[] }> =>
    adminRequest(`/engine-admin/backfill-meta?shop=${encodeURIComponent(shop)}`, adminKey, { method: 'POST', body: '{}' }),
}
